import { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import ReactMarkdown from "react-markdown";

// Static variable to control if the animation has already run
let hasAnimated = false;

const TypingText = ({
  text,
  typingDelay = 5,
  onCallbackFinish = () => {},
  componentAfterFinishTyping = null,
  sx = {},
}) => {
  const [introArray, setIntroArray] = useState([]);
  const [autoScrollIsActive, setAutoScrollIsActive] = useState(true);
  const [, setIsTypingFinished] = useState(false);
  const containerRef = useRef(null);
  const textRef = useRef(text);

  useEffect(() => {
    // If the animation has already run, simply show the complete text
    if (hasAnimated) {
      setIntroArray(text.split("\n"));
      onCallbackFinish();
      return;
    }

    // Reset state when text changes
    if (text !== textRef.current) {
      setIsTypingFinished(false);
      setIntroArray([]);
      textRef.current = text;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Guard against undefined text
    if (!text) return;

    const starterArray = text.split("\n");

    // Clear previous typing effect
    setIntroArray([]);

    const createTypingEffect = async (lineText, index) => {
      const typeCharacter = (char, i) => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setIntroArray((prevArray) => {
              // Check if the text has changed during typing
              if (textRef.current !== text) return prevArray;

              const newArray = [...prevArray];
              newArray[index] += char;
              return newArray;
            });
            resolve();
          }, typingDelay * i);
        });
      };

      const typingPromises = lineText.split("").map(typeCharacter);
      return Promise.all(typingPromises);
    };

    const cycleThroughLines = async () => {
      for (let i = 0; i < starterArray.length; i++) {
        // Check if the text has changed during typing
        if (textRef.current !== text) return;

        setIntroArray((prevArray) => [...prevArray, ""]);
        await createTypingEffect(starterArray[i], i);
      }

      onCallbackFinish();
      setIsTypingFinished(true);
    };

    cycleThroughLines();
    hasAnimated = true;

    // eslint-disable-next-line
  }, [text, typingDelay]);

  useEffect(() => {
    if (containerRef.current && autoScrollIsActive) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [introArray, autoScrollIsActive]);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      // Disable auto-scroll if the user scrolls up
      if (scrollTop + clientHeight >= scrollHeight) {
        setAutoScrollIsActive(true); // Reactivate auto-scroll
      } else {
        setAutoScrollIsActive(false); // Disable auto-scroll when scrolling up
      }
    }
  };

  const handleUserInteraction = () => {
    setAutoScrollIsActive(false);
  };
  return (
    <Typography
      component="div"
      variant="body2"
      onScroll={handleScroll}
      onMouseDown={handleUserInteraction}
      onTouchStart={handleUserInteraction}
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        ...sx,
      }}
    >
      {componentAfterFinishTyping && componentAfterFinishTyping}
      {introArray.map((introLine, index) => (
        <ReactMarkdown
          key={index}
          components={{
            h1: ({ ...props }) => (
              <h1
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            h2: ({ ...props }) => (
              <h2
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            h3: ({ ...props }) => (
              <h3
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            h4: ({ ...props }) => (
              <h4
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            h5: ({ ...props }) => (
              <h5
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            h6: ({ ...props }) => (
              <h6
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            p: ({ ...props }) => (
              <p
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            pre: ({ ...props }) => (
              <pre
                {...props}
                style={{
                  margin: "8px 0px",
                  fontSize: "revert",
                }}
              />
            ),
            ul: ({ ...props }) => (
              <ul
                {...props}
                style={{
                  fontSize: "revert",
                  margin: "revert",
                  padding: "revert",
                }}
              />
            ),
          }}
        >
          {introLine}
        </ReactMarkdown>
      ))}
    </Typography>
  );
};

export default TypingText;
